import React, { useState, useEffect } from 'react';
import { Layout, Row, Col, Card, Statistic, Typography, Divider, Button, Image, Tag, Space } from 'antd';
import { PrinterOutlined, CheckCircleOutlined, ClockCircleOutlined, WarningOutlined, ArrowRightOutlined, HomeOutlined, HeartOutlined, StarOutlined, SettingOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { Footer } = Layout;

// 导航栏分类数据（从App.tsx复制）
const categories = [
  { id: 'all', name: '全部', icon: <StarOutlined />, isSpecial: true },
  { id: 'decorative', name: '装饰', icon: null },
  { id: 'functional', name: '功能', icon: null },
  { id: 'toy', name: '玩具', icon: null },
  { id: 'educational', name: '教育', icon: null },
  { id: 'custom', name: '定制', icon: <StarOutlined />, isSpecial: true },
];

// 从后端API获取打印机列表
const fetchPrinters = async () => {
  try {
    const response = await fetch('/api/printers');
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch printers:', error);
    return [];
  }
};

// 获取打印机状态统计
const fetchPrinterStatus = async () => {
  try {
    const response = await fetch('/api/printers_status');
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch printer status:', error);
    return {
      total: 0,
      available: 0,
      printing: 0,
      maintenance: 0,
      idle: 0,
      error: 0
    };
  }
};

const PrinterOverviewPage: React.FC = () => {
  const [printers, setPrinters] = useState<any[]>([]);
  const [printerStats, setPrinterStats] = useState({
    total: 0,
    available: 0,
    printing: 0,
    maintenance: 0,
    idle: 0,
    error: 0
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [printersData, statusData] = await Promise.all([
          fetchPrinters(),
          fetchPrinterStatus()
        ]);
        
        setPrinters(printersData);
        setPrinterStats(statusData);
      } catch (error) {
        console.error('Failed to load printers:', error);
        setPrinters([]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'green';
      case 'printing': return 'blue';
      case 'maintenance': return 'orange';
      case 'idle': return 'gray';
      default: return 'red';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'available': return <CheckCircleOutlined />;
      case 'printing': return <ClockCircleOutlined />;
      case 'maintenance': return <WarningOutlined />;
      default: return <WarningOutlined />;
    }
  };

  const handleViewDetail = (printerId: number) => {
    navigate(`/printers/${printerId}`);
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
      {/* 统一Header */}
      <Row style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            padding: '16px',
            background: '#ffffff',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            borderLeft: '4px solid #d32f2f'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: '#d32f2f',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <PrinterOutlined style={{ fontSize: '24px', color: 'white' }} />
              </div>
              <Title level={3} style={{ margin: 0, color: '#d32f2f' }}>
                打印机总览
              </Title>
            </div>
            
            <Button
              type="primary"
              icon={<HomeOutlined />}
              onClick={() => navigate('/')}
              style={{
                background: '#d32f2f',
                borderColor: '#d32f2f',
                borderRadius: '20px',
                padding: '6px 20px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontWeight: '500',
                boxShadow: '0 2px 4px rgba(211, 47, 47, 0.2)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#b71c1c';
                e.currentTarget.style.borderColor = '#b71c1c';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(211, 47, 47, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#d32f2f';
                e.currentTarget.style.borderColor = '#d32f2f';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(211, 47, 47, 0.2)';
              }}
            >
              返回主页
            </Button>
          </div>
        </Col>
      </Row>

      {/* 打印机总体情况 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Title level={4} style={{ color: '#d32f2f' }}>系统概览</Title>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card 
            hoverable
            style={{ borderTop: '4px solid #d32f2f' }}
          >
            <Statistic
              title="打印机总数"
              value={printerStats.total}
              prefix={<PrinterOutlined style={{ color: '#d32f2f' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card 
            hoverable
            style={{ borderTop: '4px solid #1890ff' }}
          >
            <Statistic
              title="可用打印机"
              value={printerStats.available}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card 
            hoverable
            style={{ borderTop: '4px solid #faad14' }}
          >
            <Statistic
              title="正在打印"
              value={printerStats.printing}
              prefix={<ClockCircleOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card 
            hoverable
            style={{ borderTop: '4px solid #ff4d4f' }}
          >
            <Statistic
              title="维护中"
              value={printerStats.maintenance}
              prefix={<WarningOutlined style={{ color: '#faad14' }} />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      <Divider />

      {/* 具体打印机列表 */}
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Title level={4} style={{ color: '#d32f2f', marginBottom: '16px' }}>
            打印机列表
          </Title>
        </Col>
        
        {loading ? (
          <Col span={24}>
            <Card loading={true} />
          </Col>
        ) : printers.length === 0 ? (
          <Col span={24}>
            <Card>
              <Text type="secondary">暂无打印机数据</Text>
            </Card>
          </Col>
        ) : (
          printers.map((printer) => (
            <Col xs={24} key={printer.id} style={{ marginBottom: '16px' }}>
              <Card
                hoverable
                style={{
                  borderLeft: `4px solid ${getStatusColor(printer.status)}`,
                  borderRadius: '8px',
                }}
                bodyStyle={{ padding: '16px' }}
              >
                <Row align="middle" gutter={16}>
                  {/* 打印机缩略图 */}
                  <Col xs={24} sm={4} md={3}>
                    <div style={{
                      width: '100%',
                      height: '120px',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      background: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {printer.thumbnail_url ? (
                        <Image
                          src={printer.thumbnail_url}
                          alt={printer.name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          preview={false}
                        />
                      ) : (
                        <PrinterOutlined style={{ fontSize: '36px', color: '#d9d9d9' }} />
                      )}
                    </div>
                  </Col>

                  {/* 打印机信息 */}
                  <Col xs={24} sm={14} md={15}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Title level={5} style={{ margin: 0, color: '#1890ff' }}>
                          {printer.name}
                        </Title>
                        <Tag 
                          color={getStatusColor(printer.status)}
                          icon={getStatusIcon(printer.status)}
                          style={{ margin: 0 }}
                        >
                          {printer.status === 'available' ? '可用' : 
                           printer.status === 'printing' ? '打印中' : 
                           printer.status === 'maintenance' ? '维护中' : '空闲'}
                        </Tag>
                      </div>

                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                        <div>
                          <Text strong style={{ marginRight: '4px' }}>型号:</Text>
                          <Tag color="blue">{printer.model}</Tag>
                        </div>
                        <div>
                          <Text strong style={{ marginRight: '4px' }}>位置:</Text>
                          <Tag color="purple">{printer.location}</Tag>
                        </div>
                        <div>
                          <Text strong style={{ marginRight: '4px' }}>温度:</Text>
                          <Tag color="orange">{printer.temperature}°C</Tag>
                        </div>
                      </div>

                      {/* 支持的材料 */}
                      <div>
                        <Text strong style={{ marginRight: '8px' }}>支持材料:</Text>
                        <Space size={[4, 4]} wrap>
                          {printer.material_relations && printer.material_relations.length > 0 ? (
                            printer.material_relations.map((relation: any, idx: number) => (
                              <Tag key={idx} color="cyan">
                                {relation.material?.name || '未知材料'}
                                {relation.is_current && ' (当前)'}
                              </Tag>
                            ))
                          ) : (
                            <Text type="secondary">暂无材料信息</Text>
                          )}
                        </Space>
                      </div>
                    </Space>
                  </Col>

                  {/* 操作按钮 */}
                  <Col xs={24} sm={6} md={6}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <Button 
                        type="primary" 
                        icon={<ArrowRightOutlined />}
                        onClick={() => handleViewDetail(printer.id)}
                        style={{ background: '#d32f2f', borderColor: '#d32f2f' }}
                      >
                        查看详情
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Card>
            </Col>
          ))
        )}
      </Row>

      {/* Footer */}
      <Footer style={{ 
        background: '#1f1f1f',
        padding: '40px 0 20px 0',
        color: '#ffffff',
        borderTop: '1px solid #333',
        marginTop: '40px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', marginBottom: '30px' }}>
            {/* 左侧：平台信息 */}
            <div style={{ flex: '1', minWidth: '250px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  background: '#d32f2f',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '12px'
                }}>
                  <PrinterOutlined style={{ fontSize: '20px', color: 'white' }} />
                </div>
                <Title level={4} style={{ color: '#ffffff', margin: 0, fontWeight: 'bold' }}>
                  3D打印共享平台
                </Title>
              </div>
              <Text style={{ color: '#b0b0b0', fontSize: '14px', lineHeight: '1.6' }}>
                个人云展示墙，展示自己打印的成果，分享创意与快乐。
              </Text>
              <div style={{ marginTop: '16px' }}>
                <Space size="middle">
                  <Button 
                    type="text" 
                    icon={<HeartOutlined style={{ color: '#d32f2f' }} />}
                    style={{ color: '#b0b0b0', padding: '0' }}
                  >
                    关注我
                  </Button>
                  <Button 
                    type="text" 
                    icon={<StarOutlined style={{ color: '#ffb300' }} />}
                    style={{ color: '#b0b0b0', padding: '0' }}
                  >
                    收藏网站
                  </Button>
                </Space>
              </div>
            </div>

            {/* 中间：快速链接 */}
            <div style={{ flex: '1', minWidth: '200px', marginBottom: '20px' }}>
              <Title level={5} style={{ color: '#ffffff', marginBottom: '16px', borderBottom: '1px solid #333', paddingBottom: '8px' }}>
                快速链接
              </Title>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <a href="/" style={{ color: '#b0b0b0', textDecoration: 'none', fontSize: '14px', transition: 'color 0.3s' }}
                   onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
                   onMouseLeave={(e) => e.currentTarget.style.color = '#b0b0b0'}>
                  首页
                </a>
                <a href="/printers" style={{ color: '#b0b0b0', textDecoration: 'none', fontSize: '14px', transition: 'color 0.3s' }}
                   onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
                   onMouseLeave={(e) => e.currentTarget.style.color = '#b0b0b0'}>
                  打印机管理
                </a>
                <a href="#" style={{ color: '#b0b0b0', textDecoration: 'none', fontSize: '14px', transition: 'color 0.3s' }}
                   onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
                   onMouseLeave={(e) => e.currentTarget.style.color = '#b0b0b0'}>
                  作品展示
                </a>
                <a href="#" style={{ color: '#b0b0b0', textDecoration: 'none', fontSize: '14px', transition: 'color 0.3s' }}
                   onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
                   onMouseLeave={(e) => e.currentTarget.style.color = '#b0b0b0'}>
                  关于我们
                </a>
              </div>
            </div>

            {/* 右侧：联系信息 */}
            <div style={{ flex: '1', minWidth: '250px', marginBottom: '20px' }}>
              <Title level={5} style={{ color: '#ffffff', marginBottom: '16px', borderBottom: '1px solid #333', paddingBottom: '8px' }}>
                联系我们
              </Title>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <SettingOutlined style={{ color: '#d32f2f' }} />
                  <Text style={{ color: '#b0b0b0', fontSize: '14px' }}>技术支持: support@3dprint-share.com</Text>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <CheckCircleOutlined style={{ color: '#d32f2f' }} />
                  <Text style={{ color: '#b0b0b0', fontSize: '14px' }}>当前页面: 打印机总览</Text>
                </div>
              </div>
            </div>
          </div>

          {/* 分隔线 */}
          <Divider style={{ borderColor: '#333', margin: '20px 0' }} />

          {/* 底部：版权和备案信息 */}
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <div>
                <Text style={{ color: '#b0b0b0', fontSize: '14px' }}>
                  © {new Date().getFullYear()} 3D打印共享平台 版权所有
                </Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Text style={{ color: '#b0b0b0', fontSize: '14px' }}>
                  豫ICP备2026001797号-1
                </Text>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <EyeOutlined style={{ color: '#1890ff' }} />
                <Text style={{ color: '#b0b0b0', fontSize: '14px' }}>
                  公安备案号: 豫公网安备 41010502001234号
                </Text>
              </div>
            </div>
          </div>
        </div>
      </Footer>
    </div>
  );
};

export default PrinterOverviewPage;
